// PostImageCropModal.js – 1:1 Crop für Post-Bild (400x400, webp)
import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { getCroppedImg } from '../utils/cropImage';

/**
 * Modal: 1:1 Crop für Post-Bild (400x400, webp)
 * Props:
 * - isOpen: boolean
 * - imageSrc: string (dataURL oder URL)
 * - onClose: () => void
 * - onConfirm: async (file: File) => void
 */
export default function PostImageCropModal({ isOpen, imageSrc, onClose, onConfirm }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const aspect = 1;
    const canSave = !!imageSrc && !!croppedAreaPixels && !isSaving;

    const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleSave = useCallback(async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        setIsSaving(true);
        setError('');
        try {
            const outW = 400;
            const outH = 400;
            const blob = await getCroppedImg(imageSrc, croppedAreaPixels, outW, outH, 'image/webp', 0.92);
            const file = new File([blob], `post-image-${Date.now()}.webp`, { type: blob.type || 'image/webp' });
            await onConfirm(file);
        } catch (e) {
            setError(e?.message || 'Не удалось обработать изображение');
        } finally {
            setIsSaving(false);
        }
    }, [croppedAreaPixels, imageSrc, onConfirm]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200">
                    <div className="font-semibold text-secondary-900">Кадрирование изображения (1:1, 400×400)</div>
                    <button
                        type="button"
                        onClick={() => (!isSaving ? onClose() : null)}
                        className="text-secondary-500 hover:text-secondary-800"
                        aria-label="закрыть"
                    >
                        ✕
                    </button>
                </div>
                <div className="p-5">
                    <div className="text-sm text-secondary-600 mb-3">
                        Выберите область кадра. Изображение будет сохранено как 400×400 webp.
                    </div>
                    <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ height: 300 }}>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            objectFit="horizontal-cover"
                        />
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Масштаб</label>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-full"
                            disabled={isSaving}
                        />
                    </div>
                    {error && (
                        <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="mt-6 flex items-center justify-end gap-3">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
                            Отмена
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!canSave}>
                            {isSaving ? 'Сохранение…' : 'Сохранить'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

